type Named = { first?: string | null; last?: string | null; full?: string | null; email?: string | null };

const nameOf = ({ first, last, full, email }: Named): string => {
  const joined = `${first || ''} ${last || ''}`.trim();
  return (full || '').trim() || joined || (email || '').trim();
};

export type RepAffiliateInfo = {
  u_sales_reps_id?: string | null;
  sales_reps?: { sr_first_name?: string | null; sr_last_name?: string | null; sr_email?: string | null } | null;
  u_affiliate_id?: string | null;
  affiliate?: {
    a_first_name?: string | null;
    a_last_name?: string | null;
    a_email_id?: string | null;
    user?: { u_full_name?: string | null } | null;
  } | null;
  u_direct_affiliate_id?: string | null;
  u_indirect_affiliate_id?: string | null;
};

export type AffiliatePeople = {
  user?: Array<{ u_id: string; u_full_name?: string | null; u_first_name?: string | null; u_last_name?: string | null; u_email_id?: string | null }> | null;
  affiliates?: Array<{ a_id: string; a_first_name?: string | null; a_last_name?: string | null; a_email_id?: string | null }> | null;
};

/** Ids that still need a name lookup: the direct and indirect affiliate. */
export const affiliateIdsToLookUp = (info: RepAffiliateInfo | null | undefined): string[] =>
  [info?.u_direct_affiliate_id, info?.u_indirect_affiliate_id].filter((id): id is string => Boolean(id));

/**
 * What the profile shows for sales rep and affiliates. "None" when nothing is assigned,
 * and "Unknown" only when an id is set but its person can't be found.
 */
export function resolveRepAffiliate(info: RepAffiliateInfo | null | undefined, people: AffiliatePeople | null | undefined) {
  const personById = (id?: string | null): string => {
    if (!id) return 'None';
    const user = people?.user?.find((u) => u.u_id === id);
    if (user) return nameOf({ full: user.u_full_name, first: user.u_first_name, last: user.u_last_name, email: user.u_email_id }) || 'Unknown';
    const affiliate = people?.affiliates?.find((a) => a.a_id === id);
    if (affiliate) return nameOf({ first: affiliate.a_first_name, last: affiliate.a_last_name, email: affiliate.a_email_id }) || 'Unknown';
    return 'Unknown';
  };

  const rep = info?.sales_reps;
  const salesRep = info?.u_sales_reps_id
    ? nameOf({ first: rep?.sr_first_name, last: rep?.sr_last_name, email: rep?.sr_email }) || 'Unknown'
    : 'None';
  const aff = info?.affiliate;
  const affiliate = info?.u_affiliate_id
    ? nameOf({ full: aff?.user?.u_full_name, first: aff?.a_first_name, last: aff?.a_last_name, email: aff?.a_email_id }) || 'Unknown'
    : 'None';

  return {
    salesRep,
    affiliate,
    directAffiliate: personById(info?.u_direct_affiliate_id),
    indirectAffiliate: personById(info?.u_indirect_affiliate_id),
  };
}
